import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  CommunicationRequestBindingModel,
  CommunicationRequestViewModel,
  EnumerateResponseStatus,
  Request
} from '../../../models/request.interface';
import {RequestService} from '../../../services/request.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {FileUploadModel} from 'src/app/patient-management/patient-record/patient-picture-dialog/patient-picture.dialog';
import FileSaver from 'file-saver';
import {AccountService} from '../../../../configuration-management/services/account.service';
import {ConfirmationDialogComponent} from 'src/app/shared/confirmation-dialog/confirmation.dialog';
import {PrintSend} from 'src/app/shared/send-print-button/send-print.model';
import {EmailViewModel} from 'src/app/configuration-management/models/email.interface';
import {CommunicationViewModel} from '../../../../client-management/models/communication.interface';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-service-results',
  templateUrl: './communication-results.dialog.html',
  styleUrls: ['./communication-results.dialog.scss']
})
export class CommunicationResultsDialogComponent implements OnInit, OnDestroy {
  request: CommunicationRequestViewModel;
  communications: CommunicationViewModel[];
  initialCommunication: CommunicationViewModel; // Communication created by staff alongside the CommunicationRequest
  communication: EmailViewModel;
  formIsValid: boolean;
  selectedDate: Date;
  emailSent: boolean;
  emailSentDate: Date;
  public files: Array<FileUploadModel> = [];

  assignedUserIdsControl = new FormControl([], Validators.required);
  resultsForm = new FormGroup({
    patientId: new FormControl(''),
    clientId: new FormControl(''),
    requestId: new FormControl(''),
    assignedUserIds: this.assignedUserIdsControl,
    receiverAddress: new FormControl(''),
    receiverPhone: new FormControl(''),
    subject: new FormControl(''),
    textBody: new FormControl(''),
    responseStatus: new FormControl(''),
    urgency: new FormControl(''),
    preferredContactMethod: new FormControl(''),
    emailNotes: new FormControl(''),
    messageNumber: new FormControl(''),
    requestStatus: new FormControl('')
  });
  resultsFormChangedSubscription: Subscription;
  areNotesChanged: boolean;
  responseStatusOptions = EnumerateResponseStatus();

  constructor(
    public dialogRef: MatDialogRef<CommunicationResultsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Request,
    public snackBar: MatSnackBar,
    public requestService: RequestService,
    public accountService: AccountService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.emailSent = false;
    this.getRequest();

    this.resultsFormChangedSubscription = this.resultsForm.valueChanges.subscribe(form => {
      this.checkFormIsValid();
    });
  }

  ngOnDestroy() {
    this.resultsFormChangedSubscription.unsubscribe();
  }

  getRequest() {
    this.requestService.getRequest<CommunicationRequestViewModel>(this.data.id).subscribe(res => {
      this.request = res;
      this.communications = this.request.communications;
      this.initialCommunication = this.communications[0];
      if (this.communications[1]) { // Email has already been sent to client in response to CommunicationRequest
        this.emailSent = this.communications[1].emailSent;
        this.emailSentDate = this.communications[1].emailSentDate;
        this.resultsForm.controls.emailNotes.disable();
      }
      this.selectedDate = this.request.orderDate;
      this.resultsForm.controls.patientId.setValue(this.initialCommunication.patientId);
      this.resultsForm.controls.clientId.setValue(this.initialCommunication.clientId);
      this.resultsForm.controls.requestId.setValue(this.request.id);
      this.resultsForm.controls.assignedUserIds.setValue(this.request.assignees.map(a => a.id));
      this.resultsForm.controls.receiverAddress.setValue(this.initialCommunication.receiverAddress);
      this.resultsForm.controls.receiverPhone.setValue(this.initialCommunication.receiverPhone);
      this.resultsForm.controls.subject.setValue(this.initialCommunication.subject);
      this.resultsForm.controls.textBody.setValue(this.initialCommunication.textBody);
      this.resultsForm.controls.responseStatus.setValue(this.request.responseStatus);
      this.resultsForm.controls.urgency.setValue(this.request.urgency);
      this.resultsForm.controls.preferredContactMethod.setValue(this.request.preferredContactMethod);
      this.resultsForm.controls.emailNotes.setValue(this.request.emailNotes);
      this.resultsForm.controls.messageNumber.setValue(this.request.messageNumber);
      this.resultsForm.controls.requestStatus.setValue(this.request.requestStatus);

      this.checkFormIsValid();
    });
  }

  updateEmailNotes() {
    const emailNotes = this.resultsForm.controls.emailNotes.value;
    this.requestService.updateEmailNotes(this.data.id, emailNotes).subscribe(res => {
      this.request.emailNotes = emailNotes;
      this.modelChanged();
      this.snackBar.open('Email notes have been updated', 'Success', {
        duration: 2000,
      });
      this.getRequest();
    },
      error => {
        this.snackBar.open('Email notes could not be updated', 'Error', {
          duration: 2000,
        });
      });
  }

  checkFormIsValid() {
    this.formIsValid = this.resultsForm.valid;
  }

  sendEmail() {
    this.modelChanged();
    const updatedRequest = this.resultsForm.getRawValue() as CommunicationRequestBindingModel;
    this.requestService.respondToCommunicationRequest(updatedRequest).subscribe(
      res => {
        this.snackBar.open('Email sent', 'SUCCESS', {duration: 3000});
        this.request = null;
        this.initialCommunication = null;
        this.getRequest(); // refresh component data
      },
      error => {
        this.snackBar.open('Error sending email', 'ERROR', {duration: 3000});
      }
    );
  }

  cancel() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        header: 'Cancel Communication Request',
        body: `You are about to cancel a communication request for ${this.request.clientName} regarding their patient ${this.request.patientName}.`,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.requestService.cancellRequest(this.request.id).subscribe(res => {

          this.snackBar.open('Request has been cancelled', 'Success', {
            duration: 2000,
          });
          this.dialogRef.close('reload');
        },
          error => {
            this.snackBar.open('Request could not be cancelled', 'Error', {
              duration: 2000,
            });
          });

      } else {
        // cancel
        this.snackBar.open('Operation cancelled.', '', {
          duration: 2000,
        });
      }
    });
  }

  modelChanged() {
    this.checkFormIsValid();
  }

  onNotesChanged() {
    this.areNotesChanged = true;
  }

  save() {
    this.modelChanged();
    const updatedRequest = this.resultsForm.getRawValue() as CommunicationRequestBindingModel;

    this.requestService.updateCommunicationRequest(updatedRequest).subscribe(
      res => {
        this.snackBar.open('Request updated', 'SUCCESS', {duration: 3000});
    },
      error => {
        this.snackBar.open('Error updating request', 'ERROR', {duration: 3000});
    });
    this.dialogRef.close();
  }

  onNoClick(): void {
    this.dialogRef.close(this.areNotesChanged);
  }

  printOrSend(printSend: PrintSend) {
    printSend = JSON.parse(JSON.stringify(printSend));
    printSend.clientId = this.request.clientId;
    printSend.patientId = this.request.patientId;
    printSend.recordId = this.request.id;
    printSend.recordType = this.request.requestType;

    this.requestService.sendLabRequestResults(this.request.patientId, printSend).subscribe(blob => {
      this.snackBar.open('Print or Send Complete', 'Success', {
        duration: 2000,
      });

      if (printSend.sendToClient === true) {
        this.request.requestCommunicationStatus = 'SentToClient';
      }
    },
    error => {
      this.snackBar.open('Could not send service results', 'Error', {
        duration: 2000,
      });
    });
  }

  setModelDate() {
    this.request.patientMedicalRecord.createdTimestamp = this.selectedDate;
    this.communication.createdTimestamp = this.selectedDate; // TODO: why remove this?
  }

  getAssigneesDisplay(request: Request): string {
    return CommunicationRequestViewModel.getAssigneesDisplay(request);
  }
}
